import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import { makeStyles } from '@bequestinc/wui';
import { Breadcrumbs as MUIBreadcrumbs, Link as MuiLink } from '@mui/material';

import toTitleCase from 'utils';

const useStyles = makeStyles()(() => ({
  link: {
    color: '#0288d1',
  },
}));

interface PropTypes {
  overrideLastLabel?: string;
}

const Breadcrumbs = ({ overrideLastLabel = null }: PropTypes) => {
  const { classes } = useStyles();
  const router = useRouter();
  const [parsedUrls, setUrls] = useState([]);

  const getParsedUrl = (previousUrl: string, url: string, lastUrl: boolean) => {
    if (url === '') {
      return {
        name: 'Home',
        href: '/',
        isCurrent: lastUrl,
      };
    }

    const prevUrl = previousUrl && previousUrl[0] !== '/' ? `/${previousUrl}` : previousUrl;
    const urlHasQueryParams = url.split('?').length === 2;
    if (urlHasQueryParams) {
      const urlWithoutQueryParams = url.split('?')[0];
      return {
        name: toTitleCase(urlWithoutQueryParams),
        href: `${prevUrl}/${url}`,
        isCurrent: lastUrl,
      };
    }

    return {
      name: toTitleCase(url),
      href: `${prevUrl}/${url}`,
      isCurrent: lastUrl,
    };
  };

  useEffect(() => {
    const { asPath } = router;
    if (asPath === '' || asPath === '/') {
      setUrls([getParsedUrl('', '', true)]);
    } else {
      const urls = asPath.split('/');
      setUrls(
        urls.map((url, index) => {
          const lastIndex = index === urls.length - 1;
          if (index === 0) {
            return getParsedUrl('', url, lastIndex);
          }

          return getParsedUrl(urls[index - 1], url, lastIndex);
        }),
      );
    }
  }, []);

  return (
    <MUIBreadcrumbs separator="›">
      {parsedUrls.map((url, index) => (
        <MuiLink
          key={index}
          href={url.href}
          className={classes.link}
          sx={{ fontWeight: url.isCurrent ? 'bold' : '' }}
          component={Link}
        >
          {index === parsedUrls.length - 1 && overrideLastLabel ? overrideLastLabel : url.name}
        </MuiLink>
      ))}
    </MUIBreadcrumbs>
  );
};

Breadcrumbs.defaultProps = {
  overrideLastLabel: null,
};

export default observer(Breadcrumbs);
